import { useState, useEffect, SyntheticEvent, ReactElement } from "react";
import { Box, Grid, Card, CardContent, Typography, Button, Divider, FormControl, TextField, InputLabel, MenuItem, Select, useMediaQuery, Checkbox, FormGroup, FormControlLabel, SelectChangeEvent, Paper, CircularProgress, } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { Search as SearchIcon, Timeline as TimelineIcon } from '@mui/icons-material';
import axios from "axios";
import { NavLink } from "react-router-dom";
import Title from "../../components/Title";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ModalClientHistory from "./modals/ModalClientHistory";
import { useDrawer } from "../../components/context/DrawerContext";
import { stickyListRegionWidth } from "../../components/layouts/StickyListToolbar";
import SimpleCustomTable from "../../components/table/SimpleCustomTable";
import { SimpleColumn, SimpleRow, SimpleColumnGroup, RowId } from "../../components/table/interface";
import HeaderResponsive from "../../components/layouts/HeaderResponsive";
import EmptyStateMessage from "../../components/layouts/EmptyStateMessage";
import moment from "moment-timezone";
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import IconButtonWithTooltip from "../../components/layouts/IconButtonWithTooltip";

// Interfaces mejoradas
interface Office {
  id: number;
  name: string;
}

interface Collect {
  id: number;
  name: string;
}

interface Client {
  id: number;
  idnumber: string;
  fullname: string;
}

interface Lend {
  id: number;
  date: string;
  date_state: string;
  value: number;
  total_value: number;
  current_pay: number;
  client: Client;
  collect?: {
    id: number;
    name: string;
  };
}

interface FilterState {
  startDate: string;
  endDate: string;
  officeId: string;
  selectedCollects: number[];
}

interface InformeCancelacionProps {
  showAlert: (
    message: string,
    severity?: "error" | "info" | "success" | "warning"
  ) => void;
  showLoading: () => void;
  hideLoading: () => void;
}

// Funciones utilitarias
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(value);
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("es-CO");
};

// Componentes estilizados

const SummaryBox = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
}));

// Componente principal con lógica mejorada
const InformeCancelacion: React.FC<InformeCancelacionProps> = ({
  showAlert,
  showLoading,
  hideLoading,
}) => {
  document.title = "Reporte Préstamos";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentDate = nowOperationalDateString();
  const [datas, setDatas] = useState<any[]>([]);

  // Estados
  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate,
    endDate: currentDate,
    officeId: "",
    selectedCollects: [],
  });
  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [rows, setRows] = useState<SimpleRow[]>([]);
  const [summary, setSummary] = useState({
    totalPayments: 0,
    totalLoans: 0,
    count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const { drawerOpen } = useDrawer();

  // Efecto para cargar oficinas al montar el componente
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await axios.get("/offices");
        if (response.data && response.data.data) {
          setOffices(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching offices:", error);
        showAlert(
          "Error al cargar las oficinas. Intente de nuevo más tarde.",
          "error"
        );
      }
    };

    fetchOffices();
  }, [showAlert]);

  // Efecto para cargar cobros cuando cambia la oficina
  useEffect(() => {
    const fetchCollects = async () => {
      if (!filter.officeId) return;

      setLoading(true);
      try {
        const response = await axios.get(`/collectsoffice/${filter.officeId}`);
        if (response.data) {
          setCollectsOffice(response.data);
          // Reset collect selection when office changes
          setFilter((prev) => ({ ...prev, selectedCollects: [] }));
          setSelectAll(false);
        }
      } catch (error) {
        console.error("Error fetching collects:", error);
        showAlert(
          "Error al cargar los cobros para la oficina seleccionada.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCollects();
  }, [filter.officeId, showAlert]);

  // Manejadores de eventos
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleOfficeChange = (e: SelectChangeEvent<string>) => {
    setFilter((prev) => ({ ...prev, officeId: e.target.value }));
  };

  const handleSelectAllCollects = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    setFilter((prev) => ({
      ...prev,
      selectedCollects: checked ? collectsOffice.map((c) => c.id) : [],
    }));
  };

  const handleCollectChange = (collectId: number, checked: boolean) => {
    setFilter((prev) => {
      const newSelected = checked
        ? [...prev.selectedCollects, collectId]
        : prev.selectedCollects.filter((id) => id !== collectId);

      // Update selectAll state based on whether all collects are selected
      setSelectAll(newSelected.length === collectsOffice.length);

      return { ...prev, selectedCollects: newSelected };
    });
  };

  const validateForm = (): boolean => {
    if (filter.selectedCollects.length === 0) {
      showAlert("Debe seleccionar al menos un cobro", "warning");
      return false;
    }

    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert(
        "La fecha de inicio no puede ser mayor a la fecha final",
        "warning"
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    showLoading();
    setLoading(true);

    try {
      const response = await axios.post("/inform/cancellend", {
        start_date: filter.startDate,
        end_date: filter.endDate,
        collect_Id: filter.selectedCollects,
      });
      setDatas(response.data.data);
      //console.log("saludos,", response.data.data);
      const lends: Lend[] = response.data.data;
      //console.log("Lends fetched:", lends);
      if (!lends || lends.length === 0) {
        showAlert(
          "No se encontraron cancelaciones en el período seleccionado",
          "info"
        );
        setRows([]);
        setSummary({
          totalPayments: 0,
          totalLoans: 0,
          count: 0,
        });
        return;
      }

      const sortedLends = [...lends].sort((a, b) => {
        const collectA = (a.collect?.name || "").toLocaleLowerCase();
        const collectB = (b.collect?.name || "").toLocaleLowerCase();

        const collectCompare = collectA.localeCompare(collectB, "es");
        if (collectCompare !== 0) return collectCompare;

        const dateA = new Date(a.date_state || a.date || 0).getTime();
        const dateB = new Date(b.date_state || b.date || 0).getTime();
        return dateB - dateA;
      });

      const mappedRows: SimpleRow[] = sortedLends.map((lend) => ({
        id: lend.id as RowId,
        collect: lend.collect?.name || "",
        collectId: lend.collect?.id || 0,
        idnumber: lend.client.idnumber,
        clientName: lend.client.fullname,
        value: lend.total_value,
        current_pay: lend.current_pay,
        date_state: formatDate(lend.date_state),
        date: formatDate(lend.date),
        collect_name: lend.collect?.name || "",
        fullClient: lend.client,
      }));

      const totalValue = lends.reduce((sum, lend) => sum + Number(lend.total_value || 0), 0);
      const totalRow: SimpleRow = {
        id: 'TOTAL' as RowId,
        collect: '',
        collectId: 0,
        idnumber: '',
        clientName: 'Total',
        value: totalValue,
        current_pay: '',
        date_state: '',
        date: '',
        collect_name: '',
        fullClient: null,
        isTotal: true,
      };

      setRows([...mappedRows, totalRow]);
      showAlert(`Se encontraron ${lends.length} cancelaciones`, "success")
    } catch (error) {
      console.error("Error fetching cancellations:", error);
      showAlert(
        "Error al obtener las cancelaciones. Inténtelo de nuevo más tarde.",
        "error"
      );
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  const handleOpenModal = (client: any) => {
    setSelectedClient({
      cedula: client.idnumber?.toString() || "",
      nombre: client.fullname || "",
      direccion: client.mainaddress || "",
      telefono: client.phone || "",
      oficio: client.job || "",
      calificacion: "",
      celular: client.cellphone || "",
    });
    setOpenModal(true);
  };

  const simpleColumns: SimpleColumn[] = [
    { field: 'idnumber', headerName: 'Cédula', flex: 0.8, maxWidth: 80, minWidth: 120 },
    { field: 'clientName', headerName: 'Cliente', flex: 1.5, maxWidth: 80, minWidth: 200, wrapText: true },
    {
      field: 'collect', headerName: 'Cobro', flex: 1, minWidth: 150,
      maxWidth: 80,
    },
    {
      field: 'value',
      headerName: 'Valor',
      flex: 1,
      minWidth: 60,
      maxWidth: 60,
      align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) => row.value ?? 0,
      // renderCell: ({ row }: { row: SimpleRow }) => formatCurrency(Number(row.value ?? 0)),
    },
    {
      field: 'date', headerName: 'Fecha Préstamo', flex: 1, minWidth: 80,
      maxWidth: 80,
      align: 'right',
    },
    {
      field: 'date_state', headerName: 'Fecha Cancelado', flex: 1, minWidth: 80,
      maxWidth: 80,
      align: 'right',
    },
    {
      field: 'opt_history',
      headerName: '',
      maxWidth: '55px',
      align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) => {
        if ((row as any).isTotal) return null;
        return (
          <IconButtonWithTooltip
            title="Historial"
            color="primary"
            icon={<TimelineIcon />}
            onClick={() => handleOpenModal(row.fullClient)}
          />
        );
      },
    },
  ];

  return (
    <HeaderResponsive sx={{ py: 0, px: 0.5, pb: { xs: 10, md: 8 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Title>Informe de Cancelaciones</Title>
        <Box>
          <Button type="submit" form="form-cancelacion-fecha" variant="contained" startIcon={!isMobile && <SearchIcon />}>
            {isMobile ? <SearchIcon /> : 'Buscar'}
          </Button>
        </Box>
      </Box>
      <Card
        sx={{
          width: (t) => stickyListRegionWidth(t, drawerOpen),
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'auto',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <form id="form-cancelacion-fecha" onSubmit={handleSubmit}>
          <CardContent
            sx={{
              width: (t) => stickyListRegionWidth(t, drawerOpen),
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'auto',
              flexShrink: 0,
              scrollbarColor: '#0768c9 #d9e1ea',
              '&::-webkit-scrollbar': {
                width: 12,
                height: 12,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#d9e1ea',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#0768c9',
                borderRadius: 999,
                border: '2px solid #d9e1ea',
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: '#d9e1ea',
              },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  name="startDate"
                  size="small"
                  type="date"
                  label="Fecha Inicio"
                  value={filter.startDate}
                  onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: minYmd(filter.endDate, nowOperationalDateString()) }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  name="endDate"
                  size="small"
                  type="date"
                  label="Fecha Fin"
                  value={filter.endDate}
                  onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: filter.startDate, max: nowOperationalDateString() }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Oficina</InputLabel>
                  <Select
                    value={filter.officeId}
                    size="small"
                    label="Oficina"
                    onChange={handleOfficeChange}
                  >
                    {offices.map((office) => (
                      <MenuItem key={office.id} value={office.id}>
                        {office.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {collectsOffice.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                      Seleccionar Cobros:
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={selectAll}
                          onChange={handleSelectAllCollects}
                          indeterminate={
                            filter.selectedCollects.length > 0 &&
                            filter.selectedCollects.length <
                            collectsOffice.length
                          }
                        />
                      }
                      label="Marcar/Desmarcar todos"
                    />
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <FormGroup row>
                    {collectsOffice.map((collect) => (
                      <FormControlLabel
                        key={collect.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={filter.selectedCollects.includes(
                              collect.id
                            )}
                            onChange={(e) =>
                              handleCollectChange(collect.id, e.target.checked)
                            }
                            value={collect.id.toString()}
                          />
                        }
                        label={collect.name}
                        sx={{
                          minWidth: "200px",
                          width: isMobile ? "100%" : "auto",
                          m: 0.5,
                        }}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              )}
            </Grid>
            <Divider sx={{ my: 1 }} />
          </CardContent>
        </form>
        <Box>
          <CardContent
            sx={{
              width: (theme) => stickyListRegionWidth(theme, drawerOpen),
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'auto',
              pt: 0,
              pb: 4,
            }}
          >
            {rows.length > 0 ? (
              <SimpleCustomTable
                rows={rows}
                columns={simpleColumns}
                disableInternalScroll
                showBorders
                showCount
                exportCsv
                exportFileName="cancelaciones.csv"
                wrapCellContent
                columnGroups={[{ header: 'Opciones', fields: ['opt_history'] }]}
                getRowCellSx={({ row }) =>
                  (row as any).isTotal
                    ? {
                        backgroundColor: '#ffd3b8',
                        fontWeight: 700,
                      }
                    : undefined
                }
              />
            ) : (
              <EmptyStateMessage message="No existen registros" align="left" />
            )}
          </CardContent>
        </Box>
      </Card>
      <Box sx={{ height: { xs: 48, md: 32 } }} />
      <ModalClientHistory
        open={openModal}
        onClose={() => setOpenModal(false)}
        clientData={selectedClient}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
    </HeaderResponsive >
  );
};

export default InformeCancelacion;
